import React, { useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
	type TrackType,
	TrackType as TrackTypeEnum,
} from '../../../api/cc-smarts/models/TrackType';
import type { ContentDetails } from '../../../types/activeContent';
import type { ConfluenceTrackSourceInput } from '../../hooks/__generated__/useContextMenuActionsAddTrackMutation.graphql';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';

import { ModifyPlaylistButton } from './ModifyPlaylistButtons';
import { PlaylistItemIcon } from './PlaylistItemIcon';

export interface SuggestedItemData {
	id: string;
	title: string;
	trackType: TrackType;
	author?: string;
	avatarUrl?: string;
	spaceKey?: string;
	contentType?: string; //page, embed, whiteboard, folder
	isLive: boolean;
	emojiId?: string;
	contentId: string;
	supportingContent: ContentDetails[];
	mainContent: ContentDetails;
	trackSource: ConfluenceTrackSourceInput;
}

const messages = defineMessages({
	unknownAuthor: {
		id: 'suggested.item.unknown-author',
		defaultMessage: 'Unknown author',
		description: 'Fallback author name when a suggested item has no author',
	},
	typeBriefing: {
		id: 'suggested.item.type.briefing',
		defaultMessage: 'Briefing',
		description: 'Label for briefing type suggested items',
	},
	typeNarration: {
		id: 'suggested.item.type.narration',
		defaultMessage: 'Narration',
		description: 'Label for narration type suggested items',
	},
	defaultTitle: {
		id: 'suggested.item.default-title',
		defaultMessage: 'Untitled',
		description: 'The default title for a suggested item',
	},
});

interface SuggestedItemProps {
	data: SuggestedItemData;
}

const styles = cssMap({
	gridContainer: {
		display: 'grid',
		gridTemplateColumns: '1fr auto',
		alignItems: 'center',
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
	},
	container: {
		backgroundColor: 'transparent',
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	buttonContainer: {
		paddingRight: token('space.050'),
	},
	content: {
		flex: 1,
		textAlign: 'left',
	},
	contentWithMenu: {
		paddingRight: token('space.500'),
	},
	iconWrapper: {
		display: 'flex',
		alignSelf: 'flex-start',
		paddingTop: token('space.050'),
		flexShrink: 0,
		width: '24px',
		justifyContent: 'center',
	},
	title: {
		height: '20px',
	},
	subtitle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		height: '20px',
	},
	authorName: {
		flexShrink: 1,
		minWidth: '0px',
	},
	separator: {
		flexShrink: 0,
	},
	type: {
		flexShrink: 0,
	},
});

export const SuggestedItem = ({ data }: SuggestedItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { cloudId } = useAudioPlayerStore();
	const { title, trackType, author } = data;

	const displayTitle = title || formatMessage(messages.defaultTitle);
	const displayAuthor = author || formatMessage(messages.unknownAuthor);
	const audioType =
		trackType === TrackTypeEnum.BRIEFING
			? formatMessage(messages.typeBriefing)
			: formatMessage(messages.typeNarration);

	const titleRef = useRef<HTMLElement>(null);
	const authorRef = useRef<HTMLElement>(null);
	const [isTitleTruncated, setIsTitleTruncated] = useState(false);
	const [isAuthorTruncated, setIsAuthorTruncated] = useState(false);

	useEffect(() => {
		if (titleRef.current) {
			const element = titleRef.current;
			const isTruncated = element.scrollHeight > element.clientHeight;
			setIsTitleTruncated(isTruncated);
		}
	}, [title]);

	useEffect(() => {
		if (authorRef.current) {
			const element = authorRef.current;
			const isTruncated = element.scrollHeight > element.clientHeight;
			setIsAuthorTruncated(isTruncated);
		}
	}, [author]);

	const shouldShowTooltip = isTitleTruncated || isAuthorTruncated;
	const tooltipContent = (
		<>
			<div>{displayTitle}</div>
			<div>
				{displayAuthor} • {audioType}
			</div>
		</>
	);

	const playlistItem = (
		<Inline space="space.150" xcss={styles.gridContainer}>
			<Flex gap="space.150" alignItems="center" xcss={styles.container}>
				<Box xcss={styles.iconWrapper}>
					<PlaylistItemIcon
						emojiId={data.emojiId}
						contentType={data.contentType}
						isLive={data.isLive}
					/>
				</Box>

				<Stack xcss={styles.content}>
					<Box xcss={styles.title}>
						<Text maxLines={1} ref={titleRef}>
							<Heading as="h5" size="xsmall">
								{displayTitle}
							</Heading>
						</Text>
					</Box>

					<Flex xcss={styles.subtitle}>
						<Box xcss={styles.authorName}>
							<Text size="small" color="color.text.subtle" maxLines={1} ref={authorRef}>
								{displayAuthor}
							</Text>
						</Box>
						<Box xcss={styles.separator}>
							<Text size="small" color="color.text.subtle">
								•
							</Text>
						</Box>
						<Box xcss={styles.type}>
							<Text size="small" color="color.text.subtle">
								{audioType}
							</Text>
						</Box>
					</Flex>
				</Stack>
			</Flex>

			<Box xcss={styles.buttonContainer}>
				<ModifyPlaylistButton
					cloudId={cloudId}
					contentId={data.contentId}
					contentType={data.contentType || ''}
					title={data.title || ''}
					actionSource="playlistSuggestions"
					subtle
				/>
			</Box>
		</Inline>
	);

	return shouldShowTooltip ? (
		<Tooltip content={tooltipContent}>{playlistItem}</Tooltip>
	) : (
		playlistItem
	);
};
